import { MenuTitles } from 'constants/menu'
import { ContentLayout, PageLayout } from 'layout'
import { useContext, useEffect } from 'react'
import {
  AgxColumn,
  AgxDivider,
  AgxRow,
  AgxTypography,
  Icon_Email,
} from '@urbanx/agx.ui-components2'
import { useSelectedAgency } from 'hooks'
import './activityLog.scss'
import { useWindowSize, Breakpoints } from '@urbanx/agx-ui-components'
import { useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import getCampaignActivityLogs, {
  ActivityActionMap,
  ActivityLog,
} from 'services/functions/activities/getCampaignActivityLogs'
import dayjs from 'dayjs'
import { AuthContext } from '@urbanx/agx.auth.client'
import { IActivityLogResponseAction } from '@urbanx/agx.activities.client'

const ActivityLogView = () => {
  const { userAccount, getAuthToken } = useContext(AuthContext)
  const { campaignId } = useParams()
  const queryClient = useQueryClient()
  const windowSize = useWindowSize()
  const { agency } = useSelectedAgency()

  const { data: activityLogs } = useQuery<ActivityLog[] | undefined>({
    queryKey: [`campaign-activityLogs-${campaignId}`],
    queryFn: () => getCampaignActivityLogs(getAuthToken, campaignId ?? ''),
  })

  const getActionLabel = (action: IActivityLogResponseAction): string => {
    const actionKey = IActivityLogResponseAction[
      action
    ] as keyof typeof ActivityActionMap
    return ActivityActionMap[actionKey] ?? actionKey
  }

  const renderActionIcon = (action: IActivityLogResponseAction) => {
    switch (action) {
      case IActivityLogResponseAction.SentEmail: {
        return <Icon_Email color="var(--fg-tertiary)" width={17} height={17} />
      }

      default:
        return <Icon_Email color="var(--fg-tertiary)" width={17} height={17} />
    }
  }

  useEffect(() => {
    queryClient.invalidateQueries({
      queryKey: [`campaign-activityLogs-${campaignId}`],
    })
  }, [])

  const getActorName = (activity: ActivityLog) => {
    return `${activity.actorName.firstName}${
      activity.actorName.lastName ? ` ${activity.actorName.lastName}` : ''
    }`
  }

  return (
    <PageLayout
      agentName={userAccount?.firstName || ''}
      primaryPageTitle={agency?.name || ''}
      currentPageTitle={MenuTitles.ACTIVITY}
      isCampaignsSection={true}
    >
      <ContentLayout
        hasSecondaryMenu={false}
        secondaryActiveMenu={MenuTitles.ACTIVITY}
        isCampaigns={true}
      >
        <AgxColumn gap={'spacing-md'} fill>
          <AgxColumn gap={'spacing-3xl'} justifyContent="space-between" fill>
            <AgxTypography id={'lblActivity'} type="heading" size={'x-small'}>
              Activity
            </AgxTypography>
            <AgxDivider variant="horizontal" />
          </AgxColumn>
          <div className="hdivider"></div>
          <AgxColumn
            gap="spacing-3xl"
            padding={{ top: 'spacing-4xl', bottom: 'spacing-4xl' }}
            fill
          >
            {activityLogs?.map((activity, index) => (
              <div className="activityItem" key={index}>
                <AgxRow
                  gap={'spacing-md'}
                  padding={'spacing-xl'}
                  alignItems={
                    windowSize === Breakpoints.Mobile
                      ? 'flex-start'
                      : 'alignCenter'
                  }
                  fill
                >
                  {renderActionIcon(activity.action)}
                  <AgxRow
                    gap="spacing-lg"
                    justifyContent={
                      windowSize === Breakpoints.Mobile
                        ? 'right'
                        : 'space-between'
                    }
                    alignItems="alignCenter"
                    wrap
                    fill
                  >
                    <AgxTypography id={'a2'} type={'body_ui'} size={'small'}>
                      {getActorName(activity)}
                      &nbsp;
                      <AgxTypography
                        id={'i1'}
                        type={'body_ui_strong'}
                        size={'small'}
                      >
                        {getActionLabel(activity.action)}
                      </AgxTypography>
                      &nbsp;{activity.activityMessage}&nbsp;
                      <div className="tagTEST">
                        <AgxTypography
                          id={'a3'}
                          type={'body_ui'}
                          size={'small'}
                        >
                          {activity.recipient.recipientName ??
                            activity.recipient.emailAddress}
                        </AgxTypography>
                      </div>
                    </AgxTypography>
                    <AgxTypography
                      id={'ss'}
                      type={'body_ui'}
                      size={'small'}
                      color="var(--text-secondary)"
                    >
                      {dayjs(activity.activityDate).format(
                        'D MMM YYYY, h:mm a'
                      )}
                    </AgxTypography>
                  </AgxRow>
                </AgxRow>
              </div>
            ))}
          </AgxColumn>
        </AgxColumn>
      </ContentLayout>
    </PageLayout>
  )
}

export default ActivityLogView
